import { useState, useEffect, useCallback } from "react";
import { generateFileId, MAX_FILE_SIZE, ALLOWED_FILE_TYPES } from "../constant";
import { axiosInstance } from "../../../utils/axios";
import useUserData from "../../../Context/user/useUserData";
// Helper function to get MIME type from filename
const getMimeTypeFromFilename = (filename) => {
  if (!filename) return "application/pdf";

  const lowerName = filename.toLowerCase();

  if (lowerName.endsWith(".pdf")) {
    return "application/pdf";
  } else if (lowerName.endsWith(".docx")) {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  } else if (lowerName.endsWith(".doc")) {
    return "application/msword";
  }

  return "application/pdf";
};

export const useResumeUpload = (onUploadSuccess) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { user } = useUserData();

  useEffect(() => {
    const fetchInitialDocs = async () => {
      setIsInitialLoading(true);
      try {
        const response = await axiosInstance.post("/search/fetch_docs", {
          user_id: user?.id,
        });

        const serverDocs = response.data?.data || [];

        const mappedFiles = [];
        const initialKeywords = {};

        serverDocs.forEach((doc) => {
          const filename = doc.original_filename || "Untitled";
          const fileObj = {
            id: doc.doc_id,
            name: filename,
            size: 0,
            type: getMimeTypeFromFilename(filename),
            file: null,
            uploadedAt: doc.uploaded_at ? new Date(doc.uploaded_at) : new Date(),
            docId: doc.doc_id,
            isServerFile: true,
          };

          mappedFiles.push(fileObj);
          initialKeywords[doc.doc_id] = doc.keywords || [];
        });

        setUploadedFiles(mappedFiles);

        // Return initial data for parent to use
        if (onUploadSuccess && mappedFiles.length > 0) {
          onUploadSuccess({
            type: "initial",
            files: mappedFiles,
            keywords: initialKeywords,
          });
        }
      } catch (error) {
        console.error("Failed to fetch initial documents:", error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    if (user?.id) {
      fetchInitialDocs();
    }
  }, [user]);

  const validateFile = useCallback((file, existingFiles) => {
    const isAllowedType =
      ALLOWED_FILE_TYPES.includes(file.type) ||
      file.name.toLowerCase().endsWith(".pdf") ||
      file.name.toLowerCase().endsWith(".doc") ||
      file.name.toLowerCase().endsWith(".docx");

    if (!isAllowedType) {
      return { valid: false, error: "Please upload a PDF or Word document (.pdf, .doc, .docx)" };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
      };
    }

    if (existingFiles.some((f) => f.name === file.name)) {
      return { valid: false, error: "This file has already been added" };
    }

    return { valid: true };
  }, []);

  const uploadFileToAPI = async (file, fileId) => {
    const formData = new FormData();
    formData.append("files", file);
    formData.append("user_id", user?.id);

    try {
      const response = await axiosInstance.post("search/upload_documents", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 90) / progressEvent.total);
          setUploadingFiles((prev) => ({
            ...prev,
            [fileId]: Math.min(percentCompleted, 90),
          }));
        },
      });

      const fileResults = response.data.data || [];
      const uploadedFileData = fileResults.find(
        (r) => r.filename === file.name && r.status === "success",
      );

      if (uploadedFileData) {
        return {
          success: true,
          docId: uploadedFileData.doc_id,
          keywords: uploadedFileData.keywords || [],
        };
      } else {
        const failedData = fileResults.find((r) => r.filename === file.name);
        throw new Error(failedData?.status || "Processing failed");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.data ||
        error.response?.data?.message ||
        error.message ||
        "Upload failed";

      return { success: false, error: errorMessage };
    }
  };

  const handleFileSelect = useCallback(
    async (files) => {
      const fileArray = Array.from(files);

      for (const file of fileArray) {
        const validation = validateFile(file, uploadedFiles);
        if (!validation.valid) {
          alert(validation.error);
          continue;
        }

        const fileId = generateFileId();
        const newFile = {
          id: fileId,
          name: file.name,
          size: file.size,
          type: file.type || getMimeTypeFromFilename(file.name), // Fallback to filename detection
          file: file,
          uploadedAt: new Date(),
          docId: null,
          isServerFile: false,
        };

        setUploadedFiles((prev) => [...prev, newFile]);
        setUploadingFiles((prev) => ({ ...prev, [fileId]: 0 }));
        setUploadErrors((prev) => ({ ...prev, [fileId]: null }));

        const uploadResult = await uploadFileToAPI(file, fileId);

        if (uploadResult.success) {
          const serverDocId = uploadResult.docId;

          // Replace temp fileId with server's doc_id
          setUploadedFiles((prev) =>
            prev.map((f) => (f.id === fileId ? { ...f, id: serverDocId, docId: serverDocId } : f)),
          );

          // Transfer progress from temp id to doc_id
          setUploadingFiles((prev) => {
            const { [fileId]: _, ...rest } = prev;
            return { ...rest, [serverDocId]: 100 };
          });

          setTimeout(() => {
            setUploadingFiles((prev) => {
              const { [serverDocId]: _, ...rest } = prev;
              return rest;
            });
          }, 500);

          // Notify parent with doc_id
          if (onUploadSuccess) {
            onUploadSuccess({
              type: "upload",
              fileId: serverDocId,
              keywords: uploadResult.keywords,
            });
          }
        } else {
          setUploadErrors((prev) => ({ ...prev, [fileId]: uploadResult.error }));
          setUploadingFiles((prev) => {
            const { [fileId]: _, ...rest } = prev;
            return rest;
          });
        }
      }
    },
    [uploadedFiles, validateFile, onUploadSuccess],
  );

  const removeFile = useCallback((fileId) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    setUploadErrors((prev) => {
      const { [fileId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  return {
    uploadedFiles,
    uploadingFiles,
    uploadErrors,
    isInitialLoading,
    handleFileSelect,
    removeFile,
  };
};
